import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { 
  Paintbrush, 
  Eraser, 
  Square, 
  Circle, 
  Minus, 
  Type, 
  Palette, 
  Save, 
  FolderOpen,
  Trash2,
  Undo,
  Redo,
  Download,
  Copy,
  Clipboard,
  ZoomIn,
  ZoomOut,
  MousePointer
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';

type Tool = 'brush' | 'eraser' | 'rectangle' | 'circle' | 'line' | 'text' | 'select';

interface Point {
  x: number;
  y: number;
}

interface DrawingElement {
  id: string;
  tool: Tool;
  points: Point[];
  color: string;
  size: number;
  startPoint?: Point;
  endPoint?: Point;
  text?: string;
}

export const PaintProgram: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('brush');
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState('#000000');
  const [elements, setElements] = useState<DrawingElement[]>([]);
  const [history, setHistory] = useState<DrawingElement[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [currentElement, setCurrentElement] = useState<DrawingElement | null>(null);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [showTextPrompt, setShowTextPrompt] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<Point | null>(null);
  const { toast } = useToast();

  // Predefined colors
  const colors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#800000', '#008000', '#000080', '#800080', '#808000', '#008080', '#C0C0C0', '#808080',
    '#FF9999', '#99FF99', '#9999FF', '#FFFF99', '#FF99FF', '#99FFFF', '#FFE4B5', '#D3D3D3'
  ];

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    
    if (canvas && overlayCanvas) {
      const ctx = canvas.getContext('2d');
      const overlayCtx = overlayCanvas.getContext('2d');
      
      if (ctx && overlayCtx) {
        // Set canvas size
        canvas.width = 800;
        canvas.height = 600;
        overlayCanvas.width = 800;
        overlayCanvas.height = 600;
        
        // Set white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Set line styles
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        overlayCtx.lineCap = 'round';
        overlayCtx.lineJoin = 'round';
      }
    }
  }, []);

  // Redraw canvas
  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear and set white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw all elements
    elements.forEach(element => {
      ctx.strokeStyle = element.color;
      ctx.lineWidth = element.size;
      ctx.fillStyle = element.color;

      switch (element.tool) {
        case 'brush':
        case 'eraser':
          if (element.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(element.points[0].x, element.points[0].y);
            element.points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;
        
        case 'rectangle':
          if (element.startPoint && element.endPoint) {
            const width = element.endPoint.x - element.startPoint.x;
            const height = element.endPoint.y - element.startPoint.y;
            ctx.strokeRect(element.startPoint.x, element.startPoint.y, width, height);
          }
          break;
        
        case 'circle':
          if (element.startPoint && element.endPoint) {
            const radius = Math.sqrt(
              Math.pow(element.endPoint.x - element.startPoint.x, 2) +
              Math.pow(element.endPoint.y - element.startPoint.y, 2)
            );
            ctx.beginPath();
            ctx.arc(element.startPoint.x, element.startPoint.y, radius, 0, 2 * Math.PI);
            ctx.stroke();
          }
          break;
        
        case 'line':
          if (element.startPoint && element.endPoint) {
            ctx.beginPath();
            ctx.moveTo(element.startPoint.x, element.startPoint.y);
            ctx.lineTo(element.endPoint.x, element.endPoint.y);
            ctx.stroke();
          }
          break;
        
        case 'text':
          if (element.startPoint && element.text) {
            ctx.font = `${element.size * 4}px Arial`;
            ctx.fillText(element.text, element.startPoint.x, element.startPoint.y);
          }
          break;
      }
    });
  };

  // Handle mouse events
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    setIsDrawing(true);
    setStartPoint(pos);

    const newElement: DrawingElement = {
      id: Date.now().toString(),
      tool,
      points: tool === 'brush' || tool === 'eraser' ? [pos] : [],
      color: tool === 'eraser' ? '#FFFFFF' : brushColor,
      size: brushSize,
      startPoint: pos
    };

    if (tool === 'text') {
      setTextPosition(pos);
      setShowTextPrompt(true);
      setIsDrawing(false);
      return;
    }

    setCurrentElement(newElement);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentElement) return;

    const pos = getMousePos(e);
    const overlayCanvas = overlayCanvasRef.current;
    
    if (tool === 'brush' || tool === 'eraser') {
      const updatedElement = {
        ...currentElement,
        points: [...currentElement.points, pos]
      };
      setCurrentElement(updatedElement);

      // Draw real-time on main canvas
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : brushColor;
          ctx.lineWidth = brushSize;
          ctx.beginPath();
          const lastPoint = currentElement.points[currentElement.points.length - 1];
          ctx.moveTo(lastPoint.x, lastPoint.y);
          ctx.lineTo(pos.x, pos.y);
          ctx.stroke();
        }
      }
    } else if (overlayCanvas && startPoint) {
      // Preview shapes on overlay canvas
      const overlayCtx = overlayCanvas.getContext('2d');
      if (overlayCtx) {
        overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
        overlayCtx.strokeStyle = brushColor;
        overlayCtx.lineWidth = brushSize;
        overlayCtx.fillStyle = brushColor;

        switch (tool) {
          case 'rectangle':
            const width = pos.x - startPoint.x;
            const height = pos.y - startPoint.y;
            overlayCtx.strokeRect(startPoint.x, startPoint.y, width, height);
            break;
          
          case 'circle':
            const radius = Math.sqrt(
              Math.pow(pos.x - startPoint.x, 2) + Math.pow(pos.y - startPoint.y, 2)
            );
            overlayCtx.beginPath();
            overlayCtx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
            overlayCtx.stroke();
            break;
          
          case 'line':
            overlayCtx.beginPath();
            overlayCtx.moveTo(startPoint.x, startPoint.y);
            overlayCtx.lineTo(pos.x, pos.y);
            overlayCtx.stroke();
            break;
        }
      }
    }
  };

  const stopDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentElement) return;

    const pos = getMousePos(e);
    
    if (tool === 'brush' || tool === 'eraser') {
      const newElements = [...elements, currentElement];
      setElements(newElements);
      saveToHistory(newElements);
    } else if (startPoint) {
      const finalElement = {
        ...currentElement,
        endPoint: pos
      };
      const newElements = [...elements, finalElement];
      setElements(newElements);
      saveToHistory(newElements);
      
      // Clear overlay
      const overlayCanvas = overlayCanvasRef.current;
      if (overlayCanvas) {
        const overlayCtx = overlayCanvas.getContext('2d');
        if (overlayCtx) {
          overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
        }
      }
      
      // Redraw main canvas
      redrawCanvas();
    }

    setIsDrawing(false);
    setCurrentElement(null);
    setStartPoint(null);
  };

  const saveToHistory = (newElements: DrawingElement[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newElements]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements([...history[historyIndex - 1]]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements([...history[historyIndex + 1]]);
    }
  };

  const clearCanvas = () => {
    setElements([]);
    saveToHistory([]);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const saveImage = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const link = document.createElement('a');
      link.download = 'painting.png';
      link.href = canvas.toDataURL();
      link.click();
      
      toast({
        title: "Image Saved",
        description: "Your painting has been downloaded as painting.png",
      });
    }
  };

  const handleTextSubmit = () => {
    if (textInput.trim() && textPosition) {
      const newElement: DrawingElement = {
        id: Date.now().toString(),
        tool: 'text',
        points: [],
        color: brushColor,
        size: brushSize,
        startPoint: textPosition,
        text: textInput.trim()
      };
      
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveToHistory(newElements);
    }
    
    setShowTextPrompt(false);
    setTextInput('');
    setTextPosition(null);
  };

  // Redraw when elements change
  useEffect(() => {
    redrawCanvas();
  }, [elements]);

  const tools = [
    { id: 'select', name: 'Select', icon: MousePointer },
    { id: 'brush', name: 'Brush', icon: Paintbrush },
    { id: 'eraser', name: 'Eraser', icon: Eraser },
    { id: 'line', name: 'Line', icon: Minus },
    { id: 'rectangle', name: 'Rectangle', icon: Square },
    { id: 'circle', name: 'Circle', icon: Circle },
    { id: 'text', name: 'Text', icon: Type },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Toolbar */}
      <div className="border-b border-border bg-muted/30 p-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* File operations */}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-8">
              <FolderOpen size={14} className="mr-1" />
              Open
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={saveImage}>
              <Save size={14} className="mr-1" />
              Save
            </Button>
            <Button size="sm" variant="outline" className="h-8">
              <Download size={14} className="mr-1" />
              Export
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* History operations */}
          <div className="flex items-center gap-1">
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8" 
              onClick={undo}
              disabled={historyIndex <= 0}
            >
              <Undo size={14} />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
            >
              <Redo size={14} />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Edit operations */}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-8">
              <Copy size={14} />
            </Button>
            <Button size="sm" variant="outline" className="h-8">
              <Clipboard size={14} />
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={clearCanvas}>
              <Trash2 size={14} />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6" />

          {/* Zoom operations */}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-8">
              <ZoomIn size={14} />
            </Button>
            <Button size="sm" variant="outline" className="h-8">
              <ZoomOut size={14} />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Tool Panel */}
        <div className="w-20 border-r border-border bg-muted/20 p-2">
          <div className="space-y-1">
            {tools.map((toolOption) => {
              const IconComponent = toolOption.icon;
              return (
                <Button
                  key={toolOption.id}
                  variant={tool === toolOption.id ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "w-full h-12 flex flex-col items-center justify-center gap-1 text-xs",
                    tool === toolOption.id && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => setTool(toolOption.id as Tool)}
                  title={toolOption.name}
                >
                  <IconComponent size={16} />
                  <span className="text-[10px]">{toolOption.name}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col">
          {/* Brush Controls */}
          <div className="border-b border-border bg-muted/10 p-3">
            <div className="flex items-center gap-6">
              {/* Brush Size */}
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium min-w-fit">Size:</Label>
                <div className="flex items-center gap-3">
                  <Slider
                    value={[brushSize]}
                    onValueChange={(value) => setBrushSize(value[0])}
                    max={50}
                    min={1}
                    step={1}
                    className="w-32"
                  />
                  <span className="text-sm min-w-fit w-8">{brushSize}px</span>
                </div>
              </div>

              {/* Current Color */}
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">Color:</Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded border border-border cursor-pointer"
                    style={{ backgroundColor: brushColor }}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'color';
                      input.value = brushColor;
                      input.addEventListener('change', (e) => {
                        setBrushColor((e.target as HTMLInputElement).value);
                      });
                      input.click();
                    }}
                  />
                  <span className="text-sm font-mono">{brushColor}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Color Palette */}
          <div className="border-b border-border bg-muted/10 p-2">
            <div className="flex items-center gap-1 flex-wrap">
              <Label className="text-xs font-medium mr-2">Palette:</Label>
              {colors.map((color) => (
                <button
                  key={color}
                  className={cn(
                    "w-6 h-6 rounded border-2 cursor-pointer hover:scale-110 transition-transform",
                    brushColor === color ? "border-primary shadow-md" : "border-border"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => setBrushColor(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Canvas Container */}
          <div className="flex-1 p-4 bg-gradient-to-br from-muted/20 to-muted/5 overflow-auto">
            <div className="relative inline-block border-2 border-border shadow-lg bg-white">
              <canvas
                ref={canvasRef}
                className="block cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              <canvas
                ref={overlayCanvasRef}
                className="absolute top-0 left-0 pointer-events-none"
                style={{ mixBlendMode: 'normal' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Custom Text Prompt Dialog */}
      {showTextPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Enter Text</h3>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') {
                  setShowTextPrompt(false);
                  setTextInput('');
                  setTextPosition(null);
                }
              }}
              className="w-full p-2 border border-border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Type your text here..."
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowTextPrompt(false);
                  setTextInput('');
                  setTextPosition(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleTextSubmit}>
                Add Text
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className="border-t border-border bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>Tool: {tools.find(t => t.id === tool)?.name}</span>
            <span>Size: {brushSize}px</span>
            <span>Color: {brushColor}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Canvas: 800 × 600</span>
            <span>Elements: {elements.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};